import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  CircularProgress,
  Alert,
  Link,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  InputAdornment,
  Divider,
} from '@mui/material';
import {
  Search as SearchIcon,
  Download as DownloadIcon,
  CalendarToday as CalendarIcon,
} from '@mui/icons-material';
import { reportService, DailyAssignmentReportEntry } from '../services/reportService';
import { exportToCSV } from '../utils/csvExport';
import { useAuth } from '../contexts/AuthContext';
import { firmAPI } from '../services/api';

const today = new Date().toISOString().split('T')[0];

const DailyAssignmentReport: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();

  const isAuthorized = () => {
    if (!user?.roles) return false;
    return user.roles.some(
      (role) => role.slug === 'pmp_admin' || role.slug === 'site_admin'
    );
  };

  useEffect(() => {
    if (user && !isAuthorized()) {
      navigate('/', { replace: true });
    }
  }, [user, navigate]);

  // Filter state
  const [date, setDate] = useState<string>(today);
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');
  const [search, setSearch] = useState<string>('');
  const [clientFirmId, setClientFirmId] = useState<number | ''>('');
  const [vendorFirmId, setVendorFirmId] = useState<number | ''>('');
  const [caseStatus, setCaseStatus] = useState<string>('');
  const [useDateRange, setUseDateRange] = useState<boolean>(false);

  // Firm lists
  const [clientFirms, setClientFirms] = useState<any[]>([]);
  const [vendorFirms, setVendorFirms] = useState<any[]>([]);

  // Data state
  const [reportData, setReportData] = useState<DailyAssignmentReportEntry[]>([]);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>('');
  const [hasSearched, setHasSearched] = useState<boolean>(false);

  // Pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(30);

  useEffect(() => {
    const loadFirms = async () => {
      try {
        const [clientRes, vendorRes] = await Promise.all([
          firmAPI.getAll({ type: 'client', per_page: 1000 }),
          firmAPI.getAll({ type: 'vendor', status: 'approved', per_page: 1000 }),
        ]);
        setClientFirms(
          (clientRes.data.data?.data || clientRes.data.data || []).filter(
            (f: any) => f.type === 'client'
          )
        );
        setVendorFirms(
          (vendorRes.data.data?.data || vendorRes.data.data || []).filter(
            (f: any) => f.type === 'vendor'
          )
        );
      } catch (err) {
        console.error('Failed to load firms:', err);
      }
    };
    if (isAuthorized()) {
      loadFirms();
    }
  }, [user]);

  const buildParams = () => {
    const params: Record<string, any> = {};
    if (useDateRange) {
      if (startDate) params.start_date = startDate;
      if (endDate) params.end_date = endDate;
    } else {
      params.date = date;
    }
    if (search) params.search = search;
    if (clientFirmId) params.client_firm_id = clientFirmId;
    if (vendorFirmId) params.vendor_firm_id = vendorFirmId;
    if (caseStatus) params.case_status = caseStatus;
    return params;
  };

  const handleSearch = async () => {
    setLoading(true);
    setError('');
    setHasSearched(true);
    try {
      const result = await reportService.getDailyAssignmentReport(buildParams());
      setReportData(result.data);
      setTotalCount(result.total);
      setPage(0);
    } catch (err: any) {
      console.error('Error fetching report:', err);
      setError(err.response?.data?.message || 'Failed to fetch report');
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') handleSearch();
  };

  const handleExportCSV = () => {
    exportToCSV(
      reportData,
      [
        { key: 'assigned_at', label: 'Assigned At' },
        { key: 'client_firm_name', label: 'Client Firm' },
        { key: 'client_case_id', label: 'Client Case ID' },
        { key: 'client_case_title', label: 'Client Case Title' },
        { key: 'client_case_file_no', label: 'Client File No.' },
        { key: 'vendor_firm_name', label: 'Vendor Firm' },
        { key: 'vendor_user_name', label: 'Assigned To' },
        { key: 'vendor_case_id', label: 'Vendor Case ID' },
        { key: 'vendor_case_title', label: 'Vendor Case Title' },
        { key: 'vendor_case_file_no', label: 'Vendor File No.' },
        { key: 'status_label', label: 'Status' },
        { key: 'due_date', label: 'Due Date' },
        { key: 'project_name', label: 'Project' },
        { key: 'assigned_by_name', label: 'Assigned By' },
      ],
      `daily-assignment-report_${date || startDate || 'all'}.csv`
    );
  };

  const getStatusColor = (status: string | null) => {
    if (!status) return 'default';
    switch (status.toLowerCase()) {
      case 'open':        return 'info';
      case 'in_progress': return 'warning';
      case 'completed':   return 'success';
      case 'closed':      return 'default';
      default:            return 'default';
    }
  };

  const paginatedData = reportData.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  if (user && !isAuthorized()) {
    return (
      <Box>
        <Alert severity="error">
          You do not have permission to access this report. Only PMP Admins and Site
          Admins can view this report.
        </Alert>
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Daily Assignment Report
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Global view of all case assignments across all firms. Search by date, firm, or
        case details.
      </Typography>

      {/* Filters */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Filters
        </Typography>

        {/* Date mode toggle */}
        <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
          <Button
            size="small"
            variant={!useDateRange ? 'contained' : 'outlined'}
            onClick={() => setUseDateRange(false)}
            startIcon={<CalendarIcon />}
          >
            Single Date
          </Button>
          <Button
            size="small"
            variant={useDateRange ? 'contained' : 'outlined'}
            onClick={() => setUseDateRange(true)}
            startIcon={<CalendarIcon />}
          >
            Date Range
          </Button>
        </Box>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {/* Date row */}
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2 }}>
            {!useDateRange ? (
              <Box sx={{ flex: 1, minWidth: { xs: '100%', md: '200px' } }}>
                <TextField
                  fullWidth
                  label="Date"
                  type="date"
                  value={date}
                  onChange={(e) => setDate(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  helperText="Assignments made on this date"
                  onKeyDown={handleKeyDown}
                />
              </Box>
            ) : (
              <>
                <Box sx={{ flex: 1, minWidth: { xs: '100%', md: '200px' } }}>
                  <TextField
                    fullWidth
                    label="Start Date"
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    helperText="Assignment date from"
                    onKeyDown={handleKeyDown}
                  />
                </Box>
                <Box sx={{ flex: 1, minWidth: { xs: '100%', md: '200px' } }}>
                  <TextField
                    fullWidth
                    label="End Date"
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    helperText="Assignment date to"
                    onKeyDown={handleKeyDown}
                  />
                </Box>
              </>
            )}

            <Box sx={{ flex: 2, minWidth: { xs: '100%', md: '300px' } }}>
              <TextField
                fullWidth
                label="Search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Case title, file number, firm or vendor name"
                onKeyDown={handleKeyDown}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              />
            </Box>
          </Box>

          {/* Firm + status row */}
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2 }}>
            <Box sx={{ flex: 1, minWidth: { xs: '100%', md: '200px' } }}>
              <FormControl fullWidth>
                <InputLabel>Client Firm</InputLabel>
                <Select
                  value={clientFirmId}
                  label="Client Firm"
                  onChange={(e) => setClientFirmId(e.target.value as number | '')}
                >
                  <MenuItem value="">All</MenuItem>
                  {clientFirms.map((f: any) => (
                    <MenuItem key={f.id} value={f.id}>
                      {f.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            <Box sx={{ flex: 1, minWidth: { xs: '100%', md: '200px' } }}>
              <FormControl fullWidth>
                <InputLabel>Vendor Base</InputLabel>
                <Select
                  value={vendorFirmId}
                  label="Vendor Base"
                  onChange={(e) => setVendorFirmId(e.target.value as number | '')}
                >
                  <MenuItem value="">All</MenuItem>
                  {vendorFirms.map((f: any) => (
                    <MenuItem key={f.id} value={f.id}>
                      {f.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            <Box sx={{ flex: 1, minWidth: { xs: '100%', md: '180px' } }}>
              <FormControl fullWidth>
                <InputLabel>Case Status</InputLabel>
                <Select
                  value={caseStatus}
                  label="Case Status"
                  onChange={(e) => setCaseStatus(e.target.value)}
                >
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="open">Open</MenuItem>
                  <MenuItem value="in_progress">In Progress</MenuItem>
                  <MenuItem value="completed">Completed</MenuItem>
                  <MenuItem value="closed">Closed</MenuItem>
                </Select>
              </FormControl>
            </Box>

            <Box
              sx={{
                display: 'flex',
                gap: 1,
                minWidth: { xs: '100%', md: 'auto' },
                alignItems: 'stretch',
              }}
            >
              <Button
                variant="contained"
                color="primary"
                onClick={handleSearch}
                disabled={loading}
                startIcon={loading ? <CircularProgress size={20} /> : <SearchIcon />}
                sx={{ height: '56px', minWidth: '110px' }}
              >
                Search
              </Button>
              <Button
                variant="outlined"
                color="primary"
                onClick={handleExportCSV}
                disabled={loading || reportData.length === 0}
                startIcon={<DownloadIcon />}
                sx={{ height: '56px', minWidth: '110px' }}
              >
                Export CSV
              </Button>
            </Box>
          </Box>
        </Box>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Results */}
      <Paper sx={{ p: 3 }}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 2,
          }}
        >
          <Typography variant="h6">
            Results
            {hasSearched && (
              <Typography
                component="span"
                variant="body2"
                color="text.secondary"
                sx={{ ml: 1 }}
              >
                ({totalCount} total)
              </Typography>
            )}
          </Typography>
        </Box>

        <Divider sx={{ mb: 2 }} />

        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell><strong>Assigned At</strong></TableCell>
                <TableCell><strong>Client Firm</strong></TableCell>
                <TableCell><strong>Client Case</strong></TableCell>
                <TableCell><strong>Vendor Firm</strong></TableCell>
                <TableCell><strong>Assigned To</strong></TableCell>
                <TableCell><strong>Vendor Case</strong></TableCell>
                <TableCell><strong>Status</strong></TableCell>
                <TableCell><strong>Due Date</strong></TableCell>
                <TableCell><strong>Project</strong></TableCell>
                <TableCell><strong>Assigned By</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={10} align="center" sx={{ py: 4 }}>
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : !hasSearched ? (
                <TableRow>
                  <TableCell colSpan={10} align="center" sx={{ py: 4 }}>
                    <Typography color="text.secondary">
                      Select filters and click Search to load assignments.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : paginatedData.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={10} align="center" sx={{ py: 4 }}>
                    <Typography color="text.secondary">
                      No assignments found for the selected filters.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                paginatedData.map((entry) => (
                  <TableRow key={entry.assignment_id} hover>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      {entry.assigned_at
                        ? new Date(entry.assigned_at).toLocaleString('en-IN', {
                            dateStyle: 'medium',
                            timeStyle: 'short',
                          })
                        : 'N/A'}
                    </TableCell>

                    <TableCell>{entry.client_firm_name}</TableCell>

                    <TableCell>
                      {entry.client_case_id ? (
                        <Link
                          component="button"
                          variant="body2"
                          onClick={() =>
                            navigate(
                              `/firms/${entry.client_firm_id}/cases/${entry.client_case_id}`
                            )
                          }
                          sx={{ cursor: 'pointer', textAlign: 'left' }}
                        >
                          #{entry.client_case_id} {entry.client_case_title}
                        </Link>
                      ) : (
                        'N/A'
                      )}
                      {entry.client_case_file_no && (
                        <Typography variant="caption" display="block" color="text.secondary">
                          {entry.client_case_file_no}
                        </Typography>
                      )}
                    </TableCell>

                    <TableCell>{entry.vendor_firm_name}</TableCell>

                    <TableCell>{entry.vendor_user_name}</TableCell>

                    <TableCell>
                      {entry.vendor_case_id ? (
                        <Link
                          component="button"
                          variant="body2"
                          onClick={() =>
                            navigate(
                              `/firms/${entry.vendor_firm_id}/cases/${entry.vendor_case_id}`
                            )
                          }
                          sx={{ cursor: 'pointer', textAlign: 'left' }}
                        >
                          #{entry.vendor_case_id} {entry.vendor_case_title}
                        </Link>
                      ) : (
                        'N/A'
                      )}
                      {entry.vendor_case_file_no && (
                        <Typography variant="caption" display="block" color="text.secondary">
                          {entry.vendor_case_file_no}
                        </Typography>
                      )}
                    </TableCell>

                    <TableCell>
                      {entry.status_label && entry.status_label !== 'N/A' ? (
                        <Chip
                          label={entry.status_label}
                          color={getStatusColor(entry.vendor_case_status) as any}
                          size="small"
                        />
                      ) : (
                        'N/A'
                      )}
                    </TableCell>

                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      {entry.due_date ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <span>{entry.due_date}</span>
                          {(entry as any).is_overdue && (
                            <Chip label="Overdue" color="error" size="small" />
                          )}
                        </Box>
                      ) : 'N/A'}
                    </TableCell>

                    <TableCell>{entry.project_name}</TableCell>

                    <TableCell>{entry.assigned_by_name}</TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {reportData.length > 0 && (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              mt: 2,
            }}
          >
            <Typography variant="body2" color="text.secondary">
              Showing {page * rowsPerPage + 1}–
              {Math.min((page + 1) * rowsPerPage, reportData.length)} of{' '}
              {reportData.length} entries
            </Typography>
            <TablePagination
              component="div"
              count={reportData.length}
              page={page}
              onPageChange={(_, newPage) => setPage(newPage)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(parseInt(e.target.value, 10));
                setPage(0);
              }}
              rowsPerPageOptions={[10, 30, 50, 100]}
            />
          </Box>
        )}
      </Paper>
    </Box>
  );
};

export default DailyAssignmentReport;
